import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import type { FieldDefinition, FieldOption } from '../types';

function normalizeOptions(data: unknown): FieldOption[] {
  if (Array.isArray(data)) return data as FieldOption[];
  if (data && typeof data === 'object' && 'label' in data && 'value' in data) {
    return [data as FieldOption];
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.options)) return o.options as FieldOption[];
    if (Array.isArray(o.items)) return o.items as FieldOption[];
  }
  return [];
}

export interface RemoteSearchDynamicSelectProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: () => void;
  provider: string;
  context?: Record<string, unknown>;
  integrationId?: string;
  connectedByUserId?: string;
  disabled?: boolean;
  connected?: boolean;
  hasError: boolean;
  isDark: boolean;
}

const RemoteSearchDynamicSelect: React.FC<RemoteSearchDynamicSelectProps> = ({
  field,
  value,
  onChange,
  onFieldFocus,
  onFieldBlur,
  provider,
  context,
  integrationId,
  connectedByUserId,
  disabled,
  connected,
  hasError,
  isDark,
}) => {
  const strVal = String(value ?? '');
  const isRequired = field.validation?.required === true;

  const [options, setOptions] = useState<FieldOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);

  useEffect(() => {
    setResolvedLabel(null);
  }, [strVal]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const requestContext = useMemo(() => {
    const o: Record<string, unknown> = {
      ...(context ?? {}),
      search: debouncedSearch,
    };
    if (integrationId) {
      o.integrationId = integrationId;
    } else if (connectedByUserId) {
      o.connectedByUserId = connectedByUserId;
    }
    return o;
  }, [context, debouncedSearch, integrationId, connectedByUserId]);

  const contextStableKey = useMemo(() => JSON.stringify(requestContext), [requestContext]);

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    setLoading(true);
    const run = async () => {
      try {
        const res = await ThirdPartyIntegrationService.getDynamicOptions(
          provider,
          field.name,
          requestContext,
        );
        if (!cancelled) {
          setOptions(res?.data !== undefined ? normalizeOptions(res.data) : []);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [connected, provider, field.name, contextStableKey]);

  const selectedOption: FieldOption | null = useMemo(() => {
    if (!strVal) return null;
    const fromOptions = options.find((o) => o.value === strVal);
    if (fromOptions) return fromOptions;
    if (resolvedLabel) return { label: resolvedLabel, value: strVal };
    return null;
  }, [options, strVal, resolvedLabel]);

  const baseContextKey = useMemo(() => JSON.stringify(context ?? {}), [context]);

  useEffect(() => {
    if (!connected || !strVal) return;
    if (options.some((o) => o.value === strVal)) return;
    if (resolvedLabel) return;
    let cancelled = false;
    const run = async () => {
      try {
        const ctx: Record<string, unknown> = { ...(context ?? {}), ids: strVal };
        if (integrationId) ctx.integrationId = integrationId;
        else if (connectedByUserId) ctx.connectedByUserId = connectedByUserId;
        const res = await ThirdPartyIntegrationService.getDynamicOptions(provider, field.name, ctx);
        if (cancelled) return;
        const list = res?.data !== undefined ? normalizeOptions(res.data) : [];
        const match = list.find((o) => o.value === strVal);
        if (match) setResolvedLabel(match.label);
      } catch {
        // resolution failure leaves the raw value visible as fallback
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [connected, provider, field.name, strVal, options, baseContextKey, integrationId, connectedByUserId, resolvedLabel]);

  const displayLabel = selectedOption?.label ?? strVal ?? '';

  useEffect(() => {
    if (isFocused) return;
    setInputValue(displayLabel);
  }, [displayLabel, isFocused]);

  const inputSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiOutlinedInput-root': {
      bgcolor: isDark ? '#101828' : '#F9FAFB',
      '& fieldset': { borderColor: isDark ? '#475467' : '#D0D5DD' },
    },
  };

  const errorOutline = hasError
    ? {
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${isDark ? '#F59E0B' : '#EAB308'}`,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${isDark ? '#F59E0B' : '#EAB308'}`,
        },
      }
    : {};

  return (
    <Autocomplete<FieldOption, false, false, true>
      className="nodrag"
      freeSolo
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={(opts) => opts}
      options={options}
      loading={loading}
      disabled={disabled || !connected}
      disableClearable={isRequired}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        setInputValue(newInput);
        if (reason === 'input') {
          setSearchQuery(newInput);
          return;
        }
        if (reason === 'clear' && !isRequired) {
          onChange(field.name, '');
          setSearchQuery('');
        }
      }}
      onChange={(_, newVal) => {
        if (newVal == null) {
          if (!isRequired) {
            onChange(field.name, '');
            setSearchQuery('');
          }
          return;
        }
        if (typeof newVal === 'string') {
          onChange(field.name, newVal);
          setSearchQuery('');
          return;
        }
        onChange(field.name, newVal.value);
        setSearchQuery('');
      }}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) => {
        if (typeof val === 'string') return opt.value === val;
        return opt.value === val.value;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={field.placeholder ?? `Search ${field.label}`}
          size="small"
          onFocus={() => {
            setIsFocused(true);
            onFieldFocus?.(field.name);
          }}
          onBlur={() => {
            setIsFocused(false);
            onFieldBlur?.();
            const trimmed = inputValue.trim();
            if (!trimmed) {
              if (!isRequired) {
                onChange(field.name, '');
                setSearchQuery('');
              } else {
                setInputValue(displayLabel);
              }
              return;
            }
            const match = options.find((o) => o.value === trimmed || o.label === trimmed);
            if (match) {
              onChange(field.name, match.value);
              setSearchQuery('');
              return;
            }
            if (trimmed === displayLabel) {
              setSearchQuery('');
              return;
            }
            onChange(field.name, trimmed);
            setSearchQuery('');
          }}
          error={hasError}
          helperText={hasError ? 'Required' : undefined}
          sx={{ ...inputSx, ...errorOutline }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                    <CircularProgress color="inherit" size={18} />
                  </Box>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default RemoteSearchDynamicSelect;
